import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './menu.module.scss';

type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

interface ContextValue {
  anchorRef: React.RefObject<HTMLButtonElement>;
  open: boolean;
  setOpen: (val: boolean) => void;
  anchorId: string;
}

const ActionMenuContext = createContext<ContextValue | null>(null);

function useActionMenu() {
  const ctx = useContext(ActionMenuContext);
  if (!ctx) throw new Error('ActionMenu.* must be used inside <ActionMenu>');
  return ctx;
}

export const ActionMenu: React.FC<{ children: ReactNode }> & {
  Button: React.FC<{ children: ReactNode }>;
  Overlay: React.FC<{
    children: ReactNode;
    placement?: Placement;
    closeOnItemClick?: boolean;
    className?: string;
  }>;
} = ({ children }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const anchorId = useId();

  return (
    <ActionMenuContext.Provider value={{ anchorRef, open, setOpen, anchorId }}>
      <div className={styles.wrapper}>{children}</div>
    </ActionMenuContext.Provider>
  );
};

const Button: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { anchorRef, open, setOpen, anchorId } = useActionMenu();

  return (
    <button
      ref={anchorRef}
      id={anchorId}
      aria-haspopup="menu"
      aria-expanded={open}
      className={styles.button}
      onClick={() => setOpen(!open)}
    >
      {children}
    </button>
  );
};

const Overlay: React.FC<{
  children: ReactNode;
  placement?: Placement;
  closeOnItemClick?: boolean;
  className?: string;
}> = ({ children, placement = 'bottom-start', closeOnItemClick, className }) => {
  const { anchorRef, open, setOpen, anchorId } = useActionMenu();
  const overlayRef = useRef<HTMLDivElement>(null);
  const portalTarget = useMemo(() => document.body, []);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();

    const pos: React.CSSProperties = { position: 'absolute', zIndex: 1000 };
    if (placement.startsWith('bottom')) {
      pos.top = rect.bottom + 4;
    } else {
      pos.top = rect.top - 4;
    }
    if (placement.endsWith('start')) {
      pos.left = rect.left;
    } else {
      pos.right = window.innerWidth - rect.right;
    }
    setStyle(pos);
  }, [anchorRef, open, placement]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const anchorEl = anchorRef.current;
      const overlayEl = overlayRef.current;
      if (!overlayEl || !anchorEl) return;
      if (!overlayEl.contains(t) && !anchorEl.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, setOpen, anchorRef]);

  const onItemClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closeOnItemClick) return;
    const target = e.target as HTMLElement;
    if (target.closest("[role='menuitem']")) setOpen(false);
  };

  if (!open || !portalTarget) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={[styles.overlay, className].filter(Boolean).join(' ')}
      aria-labelledby={anchorId}
      style={style}
      onClick={onItemClick}
    >
      {children}
    </div>,
    portalTarget,
  );
};

ActionMenu.Button = Button;
ActionMenu.Overlay = Overlay;

export default ActionMenu;
